import React, { useState } from 'react';
import { Modal, Button, Spinner, Badge, Alert } from 'react-bootstrap';
import {
  approveReservation,
  confirmReservation,
  markReservationAsAttended,
  cancelReservation
} from '../../../../../../../../crud/clients_api/client_reservations';
import './ReservationModal.scss';
import { FaAt, FaPhone, FaUser, FaClock,FaCalendar, FaHashtag, FaUserFriends, FaStickyNote } from 'react-icons/fa';
import { X } from 'react-bootstrap-icons';

interface ReservationDetails {
  reservation_id: number;
  client_id: number | null; // Allow null for compatibility with ReservationModal
  client_name: string;
  email: string;
  phone_number: string;
  reservation_date: string; // Date in 'YYYY-MM-DD' format
  local_id: number;
  init: number; // Start time as a slot (e.g., 18 for 6:00 PM)
  end: number; // End time as a slot (e.g., 20 for 8:00 PM)
  n_people: number; // Number of people for the reservation
  mesa_id: number; // Table ID
  mesa_num: number; // Table number
  description: string; // Description of the reservation
  confirmed: boolean; // Whether the reservation is confirmed
  approved: boolean; // Whether the reservation is approved
  attended: boolean; // Whether the reservation was attended
  cancelled: boolean; // Whether the reservation was cancelled
  arrived: boolean; // Whether the reservation has arrived
}

interface ReservationModalProps {
  reservation: ReservationDetails;
  show: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onUpdateReservation?: (updated: ReservationDetails) => void; // NUEVO
}

const formatSlot = (slot: number) => {
  const h = Math.floor(slot / 4),
        m = (slot % 4) * 15;
  return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}`;
};

const ReservationModal: React.FC<ReservationModalProps> = ({
  reservation,
  show,
  onClose,
  onCancel,
  onUpdateReservation,
}) => {
  const [loadingAction, setLoadingAction] = useState<'approve'|'confirm'|'attend'|'cancel'|null>(null);
  const [error, setError]                 = useState<string|null>(null);

  const handleAction = async (action: 'approve'|'confirm'|'attend'|'cancel') => {
    setError(null);
    setLoadingAction(action);
    try {
      let updatedReservation = { ...reservation };
      switch(action) {
        case 'approve':
          await approveReservation(reservation.reservation_id);
          updatedReservation.approved = true;
          break;
        case 'confirm':
          await confirmReservation(reservation.reservation_id);
          await approveReservation(reservation.reservation_id); // Asegurarse de que esté aprobada antes de confirmar
          updatedReservation.approved = true;
          updatedReservation.confirmed = true;
          break;
        case 'attend':
          await markReservationAsAttended(reservation.reservation_id);
          updatedReservation.attended = true;
          break;
        case 'cancel':
          await cancelReservation(reservation.reservation_id);
          updatedReservation.cancelled = true;
          onCancel?.();
          break;
      }
      // Notificar actualización
      onUpdateReservation?.(updatedReservation);
      setTimeout(() => {
        setLoadingAction(null);
        onClose();
      }, 300);
    } catch(err: any) {
      setError(err.response?.data?.message || 'Error realizando la acción');
      setLoadingAction(null);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className="reservation-modal-container"
    >
      <Modal.Header className="rm-header">
        <Modal.Title>Reserva #{reservation.reservation_id}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="rm-body">
        {error &&
          <Alert variant="danger" className="rm-error">
            {error}
          </Alert>
        }

            <div className='actions'>

            {!reservation.approved && !reservation.cancelled && (
            <Button
              variant="none"
              onClick={() => handleAction('approve')}
              disabled={!!loadingAction}
              className='form-button-create'
            >
              {loadingAction==='approve'
                ? <Spinner animation="border" size="sm"/>
                : 'Aprobar'}
            </Button>
            )}
            {reservation.approved && !reservation.confirmed && !reservation.cancelled && (
            <Button
              variant="success"
              onClick={() => handleAction('confirm')}
              disabled={!!loadingAction}
            >
              {loadingAction==='confirm'
                ? <Spinner animation="border" size="sm"/>
                : 'Confirmar'}
            </Button>
            )}
            {reservation.confirmed && !reservation.attended && !reservation.cancelled && (
            <Button
              variant="none"
              onClick={() => handleAction('attend')}
              disabled={!!loadingAction}
              className='form-button-create'
            >
              {loadingAction==='attend'
                ? <Spinner animation="border" size="sm"/>
                : 'Atendida'}
            </Button>
            )}
            {!reservation.cancelled && (
            <Button
              variant="none"
              onClick={() => handleAction('cancel')}
              disabled={!!loadingAction}
              className='form-button-delete'
            >
              {loadingAction==='cancel'
                ? <Spinner animation="border" size="sm"/>
                : 'Cancelar'}
            </Button>
            )}
            </div>

        <div className="rm-info-grid">
         
          <div className="rm-card rm-card--client">
            <h5>Cliente</h5>
            <p><strong> <FaUser/> </strong> {reservation.client_name}</p>
            <p><strong><FaAt/></strong> {reservation.email}</p>
            <p><strong><FaPhone/></strong> {reservation.phone_number}</p>
      
            <h5>Reserva</h5>
            <p><strong><FaCalendar/></strong> {reservation.reservation_date}</p>
            <p>
              <strong><FaClock/></strong> {formatSlot(reservation.init)} – {formatSlot(reservation.end)}
            </p>
            <p><strong><FaUserFriends/></strong> {reservation.n_people}</p>
            <p><strong><FaHashtag/></strong> {reservation.mesa_num}</p>
            {reservation.description && (
              <p><strong><FaStickyNote/></strong> {reservation.description}</p>
            )}
          </div>
        </div>

        <div className="rm-status-badges">
          {reservation.approved  && <Badge bg="warning">Aprobada</Badge>}
          {reservation.confirmed && <Badge bg="success">Confirmada</Badge>}
          {reservation.attended  && <Badge bg="info">Atendida</Badge>}
          {reservation.cancelled && <Badge bg="danger">Cancelada</Badge>}
        </div>
      </Modal.Body>

      <Modal.Footer className="rm-footer">
       <div className="form-modal-footer-custom">
                <Button className="form-button-cancel" variant="none" onClick={()=>onClose()}>Cerrar</Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ReservationModal;
